import { lazy, Suspense } from 'react';
import { Navigate } from 'react-router-dom';
import { PageLoader } from 'routes/PrivateRouter';
import { revenueURLs } from 'routes/revenue/revenueURLs';

import { PermissionGuard } from 'components/PermissionGuard';

import { featureFlags } from 'utils/config/featureFlags';

const {
  FF__SHOW_REVENUE_OVERVIEW,
  FF__SHOW_REVENUE_RECONCILIATION_TAB,
  FF__SHOW_REVENUE_REPORTING_TAB,
} = featureFlags;

const Revenue = lazy(() =>
  import('pages/revenue/Revenue').then((m) => ({ default: m.Revenue }))
);

const ContractDetails = lazy(() =>
  import('pages/revenue/recognition/contract/ContractDetails').then((m) => ({
    default: m.ContractDetails,
  }))
);

const RevenueRecognitionScheduleDetail = lazy(() =>
  import('pages/revenue/recognition/shared/RevenueRecognitionScheduleDetail').then(
    (m) => ({ default: m.RevenueRecognitionScheduleDetail })
  )
);

const InvoiceDetails = lazy(() =>
  import('pages/revenue/reconciliation/invoice/InvoiceDetails').then((m) => ({
    default: m.InvoiceDetails,
  }))
);

const RevenueReportDetails = lazy(() =>
  import('pages/revenue/reporting/RevenueReportDetails').then((m) => ({
    default: m.RevenueReportDetails,
  }))
);

const RevenueElement = () => (
  <PermissionGuard moduleKey="revenue">
    <Suspense fallback={<PageLoader />}>
      <Revenue />
    </Suspense>
  </PermissionGuard>
);

const ContractDetailsElement = () => (
  <PermissionGuard moduleKey="revenue">
    <Suspense fallback={<PageLoader />}>
      <ContractDetails />
    </Suspense>
  </PermissionGuard>
);

const InvoiceDetailsElement = () => (
  <PermissionGuard moduleKey="revenue">
    <Suspense fallback={<PageLoader />}>
      <InvoiceDetails />
    </Suspense>
  </PermissionGuard>
);

const RecognitionScheduleElement = () => (
  <PermissionGuard moduleKey="revenue">
    <Suspense fallback={<PageLoader />}>
      <RevenueRecognitionScheduleDetail />
    </Suspense>
  </PermissionGuard>
);

const ReportDetailsElement = () => (
  <PermissionGuard moduleKey="revenue">
    <Suspense fallback={<PageLoader />}>
      <RevenueReportDetails />
    </Suspense>
  </PermissionGuard>
);

export const RevenueRoutes = {
  path: revenueURLs._base_relative_path,
  children: [
    // Base redirect to overview or recognition
    {
      index: true,
      element: (
        <Navigate
          to={
            FF__SHOW_REVENUE_OVERVIEW
              ? revenueURLs.overview
              : revenueURLs.recognition_contract_analysis
          }
          replace
        />
      ),
    },

    // Overview tab (feature flagged)
    ...(FF__SHOW_REVENUE_OVERVIEW
      ? [
          {
            path: revenueURLs._overview_relative_path,
            element: <RevenueElement />,
          },
        ]
      : []),

    // Recognition tab with sections
    {
      path: `${revenueURLs._recognition_relative_path}/:section?`,
      element: <RevenueElement />,
    },
    {
      path: `${revenueURLs._recognition_relative_path}/contract-analysis`,
      element: (
        <Navigate to={revenueURLs.recognition_contract_analysis} replace />
      ),
    },

    // Reconciliation tab with sections (feature flagged)
    ...(FF__SHOW_REVENUE_RECONCILIATION_TAB
      ? [
          {
            path: `${revenueURLs._reconciliation_relative_path}/:section?`,
            element: <RevenueElement />,
          },
        ]
      : []),

    // Reporting tab (feature flagged)
    ...(FF__SHOW_REVENUE_REPORTING_TAB
      ? [
          {
            path: revenueURLs._reporting_relative_path,
            element: <RevenueElement />,
          },
        ]
      : []),

    // Policies tab with sections
    {
      path: `${revenueURLs._policies_relative_path}/:section?`,
      element: <RevenueElement />,
    },

    // Detail pages
    {
      path: 'contract/:id/:tab?/:itemId?',
      element: <ContractDetailsElement />,
    },
    {
      path: 'invoice/:id',
      element: <InvoiceDetailsElement />,
    },
    {
      path: 'recognition-schedule/:id/:type?',
      element: <RecognitionScheduleElement />,
    },
    {
      path: 'reports/:id',
      element: <ReportDetailsElement />,
    },
  ],
};
